// B7 Tier 0 — owner nameplate state (plan §6A). Feeds ONE surface: the Settings
// fleet panel (/settings/nameplates) — fleet list + per-machine detail on expand +
// the enable/disable switch. `statuses` is keyed by machineId and filled lazily on
// a user-initiated row expand, never a fleet-wide render-time fan-out.
//
// NO public/anonymous document exposure — owner ruling 2026-08-13 (D14): documents
// are login-only. Do not re-add a visibility tick or a public docs list without a
// new owner ruling. The second consumer (machine-profile document ticks, B7-FE2)
// and the `adjustPublicDocumentsCount` reducer that kept its fleet count truthful
// are both gone.
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import customerNameplatesApi from "./api/customerNameplatesApi";
import { CustomerNameplatesState, NameplateStatus } from "./models/nameplateModels";

export const fetchNameplateFleet = createAsyncThunk(
  "customerNameplates/fetchFleet",
  async () => {
    return customerNameplatesApi.getFleet();
  }
);

export const fetchNameplateStatus = createAsyncThunk(
  "customerNameplates/fetchStatus",
  async (machineId: string) => {
    const status = await customerNameplatesApi.getNameplate(machineId);
    return { machineId, status };
  }
);

export const enableNameplate = createAsyncThunk(
  "customerNameplates/enable",
  async (machineId: string) => {
    const status = await customerNameplatesApi.enableNameplate(machineId);
    return { machineId, status };
  }
);

export const disableNameplate = createAsyncThunk(
  "customerNameplates/disable",
  async (machineId: string) => {
    const status = await customerNameplatesApi.disableNameplate(machineId);
    return { machineId, status };
  }
);

const initialState: CustomerNameplatesState = {
  fleet: [],
  fleetLoading: false,
  fleetError: undefined,
  statuses: {},
  statusLoading: {},
  busyMachineId: null,
  actionError: undefined,
};

/** Shared write tail: cache the fresh detail and keep the matching fleet row in
 *  step, so the table reflects the new switch state without a refetch. */
const applyStatus = (
  state: CustomerNameplatesState,
  machineId: string,
  status: NameplateStatus
) => {
  state.statuses[machineId] = status;
  const row = state.fleet.find((x) => x.machineId === machineId);
  if (row) {
    row.enabled = status.enabled;
    row.hasActiveCode = !!status.code;
  }
};

const customerNameplatesSlice = createSlice({
  name: "customerNameplates",
  initialState,
  reducers: {
    clearNameplateActionError: (state) => {
      state.actionError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNameplateFleet.pending, (state) => {
        state.fleetLoading = true;
        state.fleetError = undefined;
      })
      .addCase(fetchNameplateFleet.fulfilled, (state, { payload }) => {
        state.fleet = payload;
        state.fleetLoading = false;
      })
      .addCase(fetchNameplateFleet.rejected, (state, { error }) => {
        state.fleetError = error;
        state.fleetLoading = false;
      });

    builder
      .addCase(fetchNameplateStatus.pending, (state, { meta }) => {
        state.statusLoading[meta.arg] = true;
      })
      .addCase(fetchNameplateStatus.fulfilled, (state, { payload }) => {
        state.statusLoading[payload.machineId] = false;
        applyStatus(state, payload.machineId, payload.status);
      })
      .addCase(fetchNameplateStatus.rejected, (state, { meta }) => {
        state.statusLoading[meta.arg] = false;
      });

    builder
      .addCase(enableNameplate.pending, (state, { meta }) => {
        state.busyMachineId = meta.arg;
        state.actionError = undefined;
      })
      .addCase(enableNameplate.fulfilled, (state, { payload }) => {
        state.busyMachineId = null;
        applyStatus(state, payload.machineId, payload.status);
      })
      .addCase(enableNameplate.rejected, (state, { error }) => {
        state.busyMachineId = null;
        state.actionError = error;
      });

    builder
      .addCase(disableNameplate.pending, (state, { meta }) => {
        state.busyMachineId = meta.arg;
        state.actionError = undefined;
      })
      .addCase(disableNameplate.fulfilled, (state, { payload }) => {
        state.busyMachineId = null;
        applyStatus(state, payload.machineId, payload.status);
      })
      .addCase(disableNameplate.rejected, (state, { error }) => {
        state.busyMachineId = null;
        state.actionError = error;
      });
  },
});

export const { clearNameplateActionError } = customerNameplatesSlice.actions;

export default customerNameplatesSlice.reducer;
